/**
 * WebSocket Configuration for Quality Intelligence Feature
 *
 * Configuration values for WebSocket connections, reconnection logic,
 * and message handling in the analyzer chat.
 */

import {
  WEBSOCKET_RECONNECT_BASE_DELAY_MS,
  WEBSOCKET_RECONNECT_MAX_DELAY_MS,
  API_REQUEST_TIMEOUT_MS,
  IDLE_CHECK_INTERVAL_MS,
  SESSION_WARNING_TIMEOUT_MS,
  POLL_INTERVAL_STANDARD_MS
} from '../../../shared/constants/timeouts';

import {
  MAX_RECONNECT_ATTEMPTS as SHARED_MAX_RECONNECT_ATTEMPTS,
  MAX_PENDING_MESSAGES as SHARED_MAX_PENDING_MESSAGES
} from '../../../shared/constants/thresholds';

// ============================================================================
// CONNECTION CONFIGURATION
// ============================================================================

/**
 * Maximum number of reconnection attempts before giving up
 */
export const MAX_RECONNECT_ATTEMPTS = SHARED_MAX_RECONNECT_ATTEMPTS;

/**
 * Base delay for exponential backoff reconnection (milliseconds)
 */
export const RECONNECT_BASE_DELAY = WEBSOCKET_RECONNECT_BASE_DELAY_MS;

/**
 * Maximum delay for exponential backoff reconnection (milliseconds)
 */
export const RECONNECT_MAX_DELAY = WEBSOCKET_RECONNECT_MAX_DELAY_MS;

/**
 * Connection timeout - how long to wait for connection to establish (milliseconds)
 */
export const CONNECTION_TIMEOUT = API_REQUEST_TIMEOUT_MS;

// ============================================================================
// IDLE TIMEOUT CONFIGURATION
// ============================================================================

/**
 * Idle timeout - close connection after this much inactivity (milliseconds)
 * 10 minutes for quality intelligence (longer than standard WebSocket idle timeout)
 */
export const IDLE_TIMEOUT = 10 * SESSION_WARNING_TIMEOUT_MS;

/**
 * Activity check interval - how often to check for idle timeout (milliseconds)
 */
export const ACTIVITY_CHECK_INTERVAL = IDLE_CHECK_INTERVAL_MS;

// ============================================================================
// MESSAGE QUEUE CONFIGURATION
// ============================================================================

/**
 * Maximum number of pending messages to queue before dropping oldest
 * Prevents memory leaks when connection is lost
 */
export const MAX_PENDING_MESSAGES = SHARED_MAX_PENDING_MESSAGES;

/**
 * Message timeout - how long to wait for response (milliseconds)
 */
export const MESSAGE_TIMEOUT = SESSION_WARNING_TIMEOUT_MS;

/**
 * Chunk accumulation timeout - how long to wait for next chunk (milliseconds)
 */
export const CHUNK_TIMEOUT = POLL_INTERVAL_STANDARD_MS;

// ============================================================================
// HEARTBEAT/PING CONFIGURATION
// ============================================================================

/**
 * Heartbeat interval - how often to send ping to keep connection alive (milliseconds)
 */
export const HEARTBEAT_INTERVAL = API_REQUEST_TIMEOUT_MS;

/**
 * Pong timeout - how long to wait for pong response (milliseconds)
 */
export const PONG_TIMEOUT = POLL_INTERVAL_STANDARD_MS;

// ============================================================================
// ERROR RETRY CONFIGURATION
// ============================================================================

/**
 * Number of times to retry failed message send
 */
export const MESSAGE_RETRY_ATTEMPTS = SHARED_MAX_RECONNECT_ATTEMPTS;

/**
 * Delay between message retry attempts (milliseconds)
 */
export const MESSAGE_RETRY_DELAY = WEBSOCKET_RECONNECT_BASE_DELAY_MS;

// ============================================================================
// WEBSOCKET READY STATES
// ============================================================================

/**
 * WebSocket readyState constants for reference
 * These are standard WebSocket API values
 */
export const WS_READY_STATES = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate exponential backoff delay for reconnection attempts
 * @param {number} attempt - Current reconnection attempt number (0-indexed)
 * @returns {number} Delay in milliseconds
 */
export const calculateBackoffDelay = (attempt) => {
  const delay = RECONNECT_BASE_DELAY * Math.pow(2, attempt);
  return Math.min(delay, RECONNECT_MAX_DELAY);
};

/**
 * Check if WebSocket is in a connectable state
 * @param {WebSocket|null} ws - WebSocket instance
 * @returns {boolean} True if can attempt connection
 */
export const canConnect = (ws) => {
  if (!ws) return true;
  return ws.readyState === WS_READY_STATES.CLOSED ||
         ws.readyState === WS_READY_STATES.CLOSING;
};

/**
 * Check if WebSocket is connected and ready
 * @param {WebSocket|null} ws - WebSocket instance
 * @returns {boolean} True if connection is open
 */
export const isConnected = (ws) => {
  return ws?.readyState === WS_READY_STATES.OPEN;
};
